import { Badge, Banner, Button, DataTable, EmptyState, Hint, Panel, Select, Spinner, type Column } from '@ui';
import { useCallback, useEffect, useState } from 'react';
import { bugApi, type HostKeyInfo, type HostKeyRow } from '../bugApi';
import { copyText, SecretBlock } from '../BugReportSettings';
import { KeyIcon } from '../icons';
import { CopyValue, useConfirm } from '../jui';
import { formatDateTime } from '../labels';
import { HeaderActions } from './HeaderActions';

// Ра3, Тг1: «Трекер → Ключ хоста» — the key the project's host sends reports with, one per project, picked in the tab. A new
// key and a revoke are the operator's. A new key of the own project comes from here; its revoke does not — a revoked project
// lets nobody of it in, and the operator would stay outside.

/** Кк2: 'cli' is the server command, null — a key made before the list kept the author. */
const whoOf = (by: string | null): string => (by === 'cli' ? 'команда на сервере' : (by ?? 'автор не записан'));

export function HostKey({ project, projects = [], onChoose }: { project: string; projects?: string[]; onChoose?: (name: string) => void }) {
  const [info, setInfo] = useState<HostKeyInfo | null>(null);
  const [error, setError] = useState('');
  const [issued, setIssued] = useState<{ key: string; env: string[] } | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const { confirm, dialog } = useConfirm();

  const fail = useCallback((err: unknown, fallback: string) => setError(err instanceof Error ? err.message : fallback), []);
  useEffect(() => {
    bugApi
      .hostKey()
      .then((res) => setInfo(res.hostKey))
      .catch((err) => fail(err, 'Не удалось загрузить ключ'));
  }, [fail, project]);

  const rotate = async () => {
    const yes = await confirm({
      title: `Новый ключ для «${project}»?`,
      text: 'Это ключ хоста проекта: пароль, по которому хост отправляет репорты в сервис. Прежний перестанет работать сразу — хост получит 401, пока в его .env не встанет новый. Новый покажется один раз.',
      confirmLabel: 'Новый ключ',
      tone: 'danger',
    });
    if (!yes) return;
    setBusy(true);
    setError('');
    try {
      const res = await bugApi.rotateHostKey();
      setInfo(res.hostKey);
      setIssued({ key: res.key, env: res.env });
      setCopied(false);
    } catch (err) {
      fail(err, 'Не удалось сменить ключ');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    const yes = await confirm({
      title: `Отозвать ключ «${project}»?`,
      text: 'Хост перестанет отправлять и видеть репорты, а люди проекта не смогут войти на сайт; репорты и токены остаются, вернуть — «Новый ключ».',
      confirmLabel: 'Отозвать',
    });
    if (!yes) return;
    setBusy(true);
    setError('');
    try {
      setInfo((await bugApi.revokeHostKey()).hostKey);
      setIssued(null);
    } catch (err) {
      fail(err, 'Не удалось отозвать ключ');
    } finally {
      setBusy(false);
    }
  };

  // Why «Отозвать» is off — in words, next to the button.
  const revokeWhy = !info
    ? null
    : !info.canChange
      ? 'меняет оператор'
      : info.own
        ? 'свой проект не отозвать: вы остались бы без входа на сайт'
        : info.revokedAt
          ? `уже отозван ${formatDateTime(info.revokedAt)}`
          : null;

  // Кк1: the current key first, then the ones it replaced; an older server sends the current one only.
  const keys: HostKeyRow[] = info
    ? (info.keys ?? [{ keyPrefix: info.keyPrefix, createdAt: null, createdBy: null, state: info.revokedAt ? 'revoked' : 'works', endedReason: info.revokedAt ? 'revoked' : null, endedAt: info.revokedAt, endedBy: null, current: true }])
    : [];
  // Three columns, so the list fits a phone: the key with its state, who issued it, and how it ended — or «Отозвать» for the working one.
  const columns: Column<HostKeyRow>[] = [
    {
      key: 'prefix',
      width: '36%',
      header: 'Ключ',
      value: (row) => row.keyPrefix,
      sortable: true,
      render: (row) => (
        <span className="bug-cell-2">
          <span className="mono">{row.keyPrefix}…</span>
          <span>{row.state === 'works' ? <Badge tone="success">работает</Badge> : <Badge tone="error">отозван</Badge>}</span>
        </span>
      ),
    },
    {
      key: 'created',
      width: '32%',
      header: 'Выпущен',
      value: (row) => (row.createdAt ? new Date(row.createdAt) : null),
      sortable: true,
      render: (row) => (
        <span className="bug-cell-2 jui-muted">
          <span className="nowrap">{row.createdAt ? formatDateTime(row.createdAt) : 'не записано'}</span>
          <span>{whoOf(row.createdBy)}</span>
        </span>
      ),
    },
    {
      key: 'ended',
      width: '32%',
      header: 'Завершён',
      value: (row) => (row.endedAt ? new Date(row.endedAt) : null),
      sortable: true,
      filterable: false,
      render: (row) =>
        row.current && row.state === 'works' ? (
          <Button size="sm" variant="danger" disabled={busy || !!revokeWhy} title={revokeWhy ?? undefined} onClick={() => void revoke()}>
            Отозвать
          </Button>
        ) : row.endedAt ? (
          <span className="bug-cell-2 jui-muted">
            <span className="nowrap">
              {row.endedReason === 'replaced' ? 'заменён' : 'отозван'} {formatDateTime(row.endedAt)}
            </span>
            {row.endedBy && <span>{whoOf(row.endedBy)}</span>}
          </span>
        ) : (
          <span className="jui-muted">—</span>
        ),
    },
  ];

  return (
    <div className="jui-stack">
      {info && (
        <HeaderActions>
          <Button size="sm" variant="primary" icon={<KeyIcon />} disabled={busy || !info.canChange} title={info.canChange ? undefined : 'Ключ меняет оператор'} onClick={() => void rotate()}>
            Новый ключ
          </Button>
        </HeaderActions>
      )}
      {error && <Banner tone="error">{error}</Banner>}
      {/* Тг4: the key is one per project; the tab offers every project the person may reach. */}
      {projects.length > 1 && onChoose && (
        <Select label="Проект" size="sm" className="bug-key-project" value={project} onChange={onChoose} options={projects.map((name) => ({ value: name, label: name }))} />
      )}
      {issued && (
        <SecretBlock
          title={`Ключ хоста «${project}»`}
          note="Показывается один раз: скопируйте сейчас, потом его не увидеть — только выпустить новый."
          actions={
            <>
              <Button size="sm" variant="primary" onClick={() => copyText(issued.env.join('\n'), setCopied)}>
                {copied ? 'Скопировано' : 'Копировать строки'}
              </Button>
              <Button size="sm" variant="quiet" onClick={() => setIssued(null)}>
                Скрыть
              </Button>
            </>
          }
        >
          <div className="jui-field">
            <span className="jui-label">Что это · пароль хоста «{project}» к сервису репортов</span>
            <CopyValue value={issued.key} />
          </div>
          <div className="jui-field">
            <span className="jui-label">Куда вставить · строки в .env хоста, затем перезапустить хост</span>
            <div className="jui-stack">
              {issued.env.map((line) => (
                <CopyValue key={line} value={line} />
              ))}
            </div>
          </div>
        </SecretBlock>
      )}
      {!info ? (
        error ? <EmptyState compact>Ключ не загрузился.</EmptyState> : <Spinner label="Загрузка" size="sm" />
      ) : (
        <div className="jui-stack">
          <Panel title={`Ключ хоста · ${info.name}`}>
            <div className="jui-stack">
              <div className="jui-field">
                <span className="jui-label">Что это</span>
                <span>Пароль хоста этого проекта: по нему хост отправляет репорты в сервис. Сам ключ не хранится — виден только его начало.</span>
              </div>
              <div className="jui-field">
                <span className="jui-label">Куда вставить</span>
                <span>
                  В <span className="mono">.env</span> хоста: <span className="mono">BUG_PROJECT_KEY</span>, рядом адрес сервиса, затем перезапуск хоста.
                </span>
                <CopyValue value={info.serviceUrl} shown={`BUG_SERVICE_URL=${info.serviceUrl}`} />
              </div>
              {!info.canChange && <Hint>Новый ключ выпускает и отзывает оператор. Вы видите только состояние.</Hint>}
              {info.canChange && info.own && <Hint>Свой проект: новый ключ — отсюда; отозвать нельзя, иначе закроется вход на сайт и вам. Отзывает оператор другого проекта или команда project на сервере.</Hint>}
            </div>
          </Panel>
          <DataTable className="bug-fixed-table" label="Ключи хоста" columns={columns} rows={keys} rowKey={(row) => `${row.keyPrefix}:${row.createdAt ?? ''}:${row.current}`} pageSize={0} />
        </div>
      )}
      {dialog}
    </div>
  );
}
